import { type FC, useCallback, useMemo, useState } from "react";

import { Colors } from "@snapchat/snap-design-system";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { FormatOptions, Scale, formatBigNumber } from "../../../../lens-stats/formatters";
import { useDimensionBreakdownFunnel } from "../../../hooks";
import { ChartPaper } from "../../shared";
import { Container, Header, SubTitle, Title } from "../funnelTab.styles";

import { BarWithFunnelLabel } from "./BarWithFunnelLabel";
import { CustomLabel } from "./CustomLabel";
import { CustomizedAxisTick } from "./CustomizedXAxisTick";
import { EngagementFunnelLegend } from "./EngagementFunnelLegend";
import { EngagementFunnelTooltip } from "./EngagementFunnelTooltip";
import { BAR_WIDTH, CHART_WIDTH, ChartContainer, tickStyle, xAxisPoints } from "./engagementFunnel.styles";

export const EngagementFunnel: FC<{ className?: string }> = ({ className = "" }) => {
    const { dimensionFunnelMap } = useDimensionBreakdownFunnel();
    const data = useMemo(() => Object.values(dimensionFunnelMap["TOTAL"]?.["TOTAL"] ?? {}), [dimensionFunnelMap]);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const handleMouseOver = useCallback((_: unknown, index: number) => {
        setActiveIndex(index);
    }, []);

    return (
        <Container className={className}>
            <Header>
                <div>
                    <Title>Engagement Funnel</Title>
                    <SubTitle>Learn about how Snapchatters are engaging with your Lens throughout the funnel</SubTitle>
                </div>
            </Header>
            <ChartPaper padding="18px 0 40px">
                <ChartContainer>
                    <EngagementFunnelLegend />
                    <ResponsiveContainer width="100%" height={420}>
                        <BarChart
                            width={CHART_WIDTH}
                            height={420}
                            data={data}
                            margin={{ top: 5, left: 0, bottom: 5, right: 58 }}
                        >
                            <CartesianGrid
                                strokeDasharray="1 6"
                                stroke={Colors.Gray.V300}
                                horizontal={true}
                                vertical={true}
                                verticalPoints={xAxisPoints}
                                width={CHART_WIDTH}
                            />
                            <Bar
                                radius={[4, 4, 0, 0]}
                                dataKey="value"
                                barSize={BAR_WIDTH}
                                fill={Colors.Black.V50}
                                label={<CustomLabel />}
                                shape={<BarWithFunnelLabel />}
                                onMouseOver={handleMouseOver}
                            >
                                {data.map((_, index) => (
                                    <Cell
                                        fill={index === activeIndex ? Colors.Yellow.V200 : Colors.Black.V50}
                                        key={`cell-${index}`}
                                    />
                                ))}
                            </Bar>

                            <Tooltip cursor={false} content={EngagementFunnelTooltip} />

                            <YAxis
                                tickFormatter={(value: number) => formatBigNumber(value, FormatOptions[Scale.k])}
                                tick={tickStyle}
                                strokeWidth={0}
                                interval={0}
                            />
                            <XAxis orientation="top" strokeWidth={0} dataKey="label" tick={<CustomizedAxisTick />} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>
            </ChartPaper>
        </Container>
    );
};
